import { Koi } from '@ksyos/koi';
import type { ISchemaContainer } from '@common/validation/schemaContainer';
import type { IProcedureAddEdit } from '@frontend/components/pages/procedures/ProcedureItemAddEdit';
import { type IOphthalmologyAsset, ophthalmologyFile } from '@common/validation/customControls/ophthalmology';
import { validate } from '@frontend/utils/validation';

type ReferralCheckBase = {
    draftCareProviderOrganizationId: string | null;
    draftCareProviderPractitionerId: string | null;
    files: IOphthalmologyAsset[] | [];
};
export type ReferralCheck = {
    huisarts_betrokkenheid: string | null;
    huisarts_is_betrokken: boolean | null;
} & ReferralCheckBase;

export type ReferralCheckRequired = {
    verwijsbrief_beschikbaar: boolean | null;
} & ReferralCheckBase;

const careProviderSchema = Koi.object({
    draftCareProviderOrganizationId: Koi.string().uuid().required(),
    draftCareProviderPractitionerId: Koi.string().uuid().required(),
});

const referralCheckSchema: ISchemaContainer<ReferralCheck> = {
    schema: Koi.object({
        files: Koi.array().items(ophthalmologyFile),
        huisarts_betrokkenheid: Koi.when('huisarts_is_betrokken', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.string().max(255).required(),
        }),
        huisarts_is_betrokken: Koi.boolean().required(),
    }).concat(careProviderSchema),
};

const referralCheckRequiredSchema: ISchemaContainer<ReferralCheckRequired> = {
    schema: Koi.object({
        files: Koi.when('verwijsbrief_beschikbaar', {
            is: true,
            otherwise: Koi.array().items(ophthalmologyFile),
            then: Koi.array().items(ophthalmologyFile).min(1),
        }),
        verwijsbrief_beschikbaar: Koi.boolean().required(),
    }).concat(careProviderSchema),
};

export function validateReferralCheck({
    hasCareGroupTOOFinancingAgreement,
    isDirectProcedureAdded,
    isReferralCheckRequired,
    procedure,
}: {
    hasCareGroupTOOFinancingAgreement: boolean;
    isDirectProcedureAdded: boolean;
    isReferralCheckRequired: boolean;
    procedure: Pick<
        IProcedureAddEdit,
        'draftCareProviderOrganizationId' | 'draftCareProviderPractitionerId' | 'customControlData'
    >;
}) {
    const { files, huisarts_betrokkenheid, huisarts_is_betrokken, verwijsbrief_beschikbaar } =
        procedure.customControlData;
    const referralCheckFormValues = {
        draftCareProviderOrganizationId: procedure.draftCareProviderOrganizationId,
        draftCareProviderPractitionerId: procedure.draftCareProviderPractitionerId,
        files,
        ...(isReferralCheckRequired ? { verwijsbrief_beschikbaar } : { huisarts_betrokkenheid, huisarts_is_betrokken }),
    };

    if (!isDirectProcedureAdded || hasCareGroupTOOFinancingAgreement) {
        return validate({ schema: careProviderSchema }, referralCheckFormValues);
    }
    if (isReferralCheckRequired) {
        // referralCheck
        return validate(referralCheckRequiredSchema, referralCheckFormValues);
    }

    return validate(referralCheckSchema, referralCheckFormValues);
}
